import { useCallback, useState } from 'react';
import { Trans } from '@lingui/macro';
import { Button, Center, LoadingOverlay, Stack, Text } from '@mantine/core';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { Link } from 'react-router-dom';

import { UiCard } from '../../components/ui/ui-card/ui-card';
import { useApiClient } from '../../core/libs/api/api-client.provider';
import { HttpResource } from '../../core/libs/http-resource';
import { ChangePassForm } from '../../domains/reset-password/change-pass.form';
import { LoginForm } from '../../domains/reset-password/login.form';
import { resetPasswordRequest } from '../../domains/reset-password/requests/reset-password.request';
import { AppNotify } from '../../features/app-notify/app-notify';
import { MainSideMenu } from '../../features/main-side-menu/main-side-menu';
import { routeSignIn, routeSignUp } from '../../router/routes';

import styles from './sign-in.page.module.css';

const enum EResetPasswordSteps {
  inputLogin = 1,
  inputConfirmCode = 2,
  finish = 3,
}

type StepObservable = {
  value: EResetPasswordSteps;
  set(v: EResetPasswordSteps): void;
};

type FieldsObservable = {
  phone: string;
  code: string;
  password: string;

  setPhone(v: string): void;
  setFields(v: Pick<FieldsObservable, 'code' | 'password' | 'phone'>): void;
};

type ResetPasswordRequestParams =
  | { phone: string }
  | { phone: string; code: string; password: string };

export const ResetPasswordPage = observer(() => {
  const step = useLocalObservable<StepObservable>(() => ({
    value: EResetPasswordSteps.inputLogin,
    set(v) {
      this.value = v;
    },
  }));

  const fields = useLocalObservable<FieldsObservable>(() => ({
    phone: '',
    code: '',
    password: '',

    setPhone(v) {
      this.phone = v;
    },
    setFields(v) {
      this.phone = v.phone;
      this.code = v.code;
      this.password = v.password;
    },
  }));

  const apiClient = useApiClient();

  const [sendPhoneResource] = useState(
    () =>
      new HttpResource<ResetPasswordRequestParams, any>({
        apiClient,
        httpRequest: resetPasswordRequest,
        errorNotify: new AppNotify(),
        onFinish() {
          step.set(EResetPasswordSteps.inputConfirmCode);
        },
      })
  );

  const [changePassResource] = useState(
    () =>
      new HttpResource<ResetPasswordRequestParams, any>({
        apiClient,
        httpRequest: resetPasswordRequest,
        errorNotify: new AppNotify(),
        onFinish() {
          step.set(EResetPasswordSteps.finish);
        },
      })
  );

  const handlePhoneSubmit = useCallback(
    (values: { phone: string }) => {
      fields.setPhone(values.phone);
      sendPhoneResource.send(values);
    },
    [fields, sendPhoneResource]
  );

  const handlePassChangeSubmit = useCallback(
    (values: { phone: string; code: string; password: string }) => {
      fields.setPhone(values.phone);
      changePassResource.send(values);
    },
    [changePassResource, fields]
  );

  const isLoginRequired = step.value === EResetPasswordSteps.inputLogin;
  const isConfirmCodeRequired =
    step.value === EResetPasswordSteps.inputConfirmCode;
  const isFinished = step.value === EResetPasswordSteps.finish;
  const isNotFinished = step.value !== EResetPasswordSteps.finish;

  const isLoading = sendPhoneResource.isLoading || changePassResource.isLoading;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.headerTitle}>
          <Trans>Сброс пароля</Trans>
        </h1>
        <MainSideMenu />
      </header>
      <main className={styles.main}>
        <LoadingOverlay visible={isLoading} />
        <Stack>
          {isNotFinished && (
            <UiCard>
              <Trans>
                На следующем шаге нужно ввести код подтверждения и новый пароль
              </Trans>
              .
              <br />
              <Trans>
                Мы отправим код подтверждения на указанный номер телефона
              </Trans>
              .
            </UiCard>
          )}

          {isLoginRequired && (
            <UiCard>
              <LoginForm onSubmit={handlePhoneSubmit} />
            </UiCard>
          )}

          {isConfirmCodeRequired && (
            <UiCard>
              <ChangePassForm
                onSubmit={handlePassChangeSubmit}
                initialValues={fields}
              />
            </UiCard>
          )}

          {isFinished && (
            <UiCard>
              <Stack>
                <Text>
                  <Trans>Пароль был успешно изменен</Trans>.
                </Text>
                <Text>
                  <Trans>
                    Теперь вы можете войти в личный кабинет используя новый
                    пароль
                  </Trans>
                  .
                </Text>
                <Button
                  component={Link}
                  to={routeSignIn.path}
                  size="lg"
                  mt="xs"
                  fullWidth
                >
                  <Trans>Войти в личный кабинет</Trans>
                </Button>
              </Stack>
            </UiCard>
          )}

          {isNotFinished && (
            <UiCard>
              <Center>
                <Text>
                  <Trans>Еще не зарегистрированны?</Trans>
                </Text>
              </Center>
              <Button
                component={Link}
                to={routeSignUp.path}
                size="lg"
                mt="xs"
                fullWidth
                variant="outline"
              >
                <Trans>Зарегистрироваться</Trans>
              </Button>
            </UiCard>
          )}
        </Stack>
      </main>
    </div>
  );
});

ResetPasswordPage.displayName = 'ResetPasswordPage';
